import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type JSX } from 'react';
import { ApiError, NetworkError, userErrorMessage } from '../api/errors.js';
import {
  narrationsApi,
  type NarrationDto,
  type NarrationVoiceSelection,
  type VoiceDto,
} from '../api/narrations.js';
import { uploadAudio } from '../api/uploads.js';
import { Link } from 'react-router';
import { EmptyState } from '../components/EmptyState.js';
import { GenerationFailed } from '../components/GenerationFailed.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';
import type { Dictionary } from '../i18n/registry.js';
import { measureAudioMs, sampleLengthProblem } from './voice-sample.js';

const AUDIO_FILE_ACCEPT = 'audio/*,.ogg,.oga,.opus,.webm,.mp3,.wav,.m4a,.mp4';
type SaveState = 'idle' | 'saving' | 'saved';
type VoiceSampleMethod = 'record' | 'upload-script' | 'upload-other';

export function NarrationStep({ bookId }: { readonly bookId: string }): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const voices = useQuery({ queryKey: ['voices'], queryFn: narrationsApi.voices });
  const narrations = useQuery({
    queryKey: ['narrations', bookId],
    queryFn: () => narrationsApi.list(bookId),
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (item) => item.status === 'queued' || item.status === 'generating',
      )
        ? 2_000
        : false,
  });
  const [selectedVoice, setSelectedVoice] = useState('preset:female');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const voiceList = useRef<HTMLDivElement | null>(null);
  const addButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const ready = voices.data?.items.filter((voice) => voice.status === 'ready') ?? [];
    if (
      selectedVoice.startsWith('custom:') &&
      !ready.some((voice) => `custom:${voice.id}` === selectedVoice)
    ) {
      setSelectedVoice('preset:female');
    }
  }, [selectedVoice, voices.data]);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['voices'] }),
      queryClient.invalidateQueries({ queryKey: ['narrations', bookId] }),
      queryClient.invalidateQueries({ queryKey: ['book-preview', bookId] }),
    ]);
  };
  // Which spread an action came from, so its error is shown on that spread: under the
  // whole-book button it was a screen or more above a spread acted on far down the page.
  const [errorSceneId, setErrorSceneId] = useState<string | null>(null);
  const action = useMutation({
    mutationFn: async (input: { run: () => Promise<unknown>; sceneId: string | null }) =>
      input.run(),
    onSuccess: refresh,
    onError: (cause, input) => {
      setErrorSceneId(input.sceneId);
      setError(actionError(cause, t, t.narration.actionFailed));
    },
  });
  // The form takes the place of its button: focus goes back to whatever now stands there.
  const closeCreator = (voiceId: string | null): void => {
    setAdding(false);
    if (voiceId) setSelectedVoice(`custom:${voiceId}`);
    requestAnimationFrame(() => {
      const radio = voiceId
        ? voiceList.current?.querySelector<HTMLInputElement>(`input[value="custom:${voiceId}"]`)
        : null;
      (radio ?? addButton.current)?.focus();
    });
  };

  if (voices.isPending || narrations.isPending) return <p>{t.common.loading}</p>;
  if (!narrations.data) return <p role="alert">{t.common.unexpectedError}</p>;

  const items = narrations.data.items;
  const previewForPreset = (presetVoice: 'female' | 'male'): string | null =>
    items.find((item) => item.presetVoice === presetVoice && item.status === 'ready' && item.audio)
      ?.audio?.url ?? null;
  const presets = [
    { value: 'preset:female', name: t.narration.femaleVoice, preview: previewForPreset('female') },
    { value: 'preset:male', name: t.narration.maleVoice, preview: previewForPreset('male') },
  ];

  return (
    <section>
      <h1 className="text-2xl font-semibold">{t.narration.title}</h1>

      <div ref={voiceList} className="mt-6 grid max-w-2xl gap-2">
        <fieldset className="grid gap-2">
          <legend className="mb-2 font-semibold">{t.narration.voiceForSynthesis}</legend>
          {presets.map((preset) => (
            <VoiceRow
              key={preset.value}
              value={preset.value}
              name={preset.name}
              previewUrl={preset.preview}
              selected={selectedVoice === preset.value}
              onSelect={setSelectedVoice}
            />
          ))}
          {(voices.data?.items ?? []).map((voice) => (
            <VoiceRow
              key={voice.id}
              value={`custom:${voice.id}`}
              name={voice.name}
              previewUrl={voice.sample?.url ?? null}
              selected={selectedVoice === `custom:${voice.id}`}
              onSelect={setSelectedVoice}
              voice={voice}
              onChanged={refresh}
            />
          ))}
        </fieldset>
        {adding ? (
          <VoiceCreator
            onChanged={refresh}
            onDone={(voiceId) => closeCreator(voiceId)}
            onCancel={() => closeCreator(null)}
          />
        ) : (
          <button
            ref={addButton}
            type="button"
            className="rounded-lg border border-dashed border-[var(--kitup-color-border)] p-3 text-left font-medium text-[var(--kitup-color-accent)]"
            onClick={() => setAdding(true)}
          >
            + {t.narration.useOwnVoice}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        // Voices can be added before the story exists; narrating needs its spreads.
        <div className="mt-8">
          <EmptyState
            title={t.narration.empty}
            action={
              <Link to="?step=story" className="kitup-secondary-button">
                {t.builder.goToStep(t.builder.steps.story)}
              </Link>
            }
          />
        </div>
      ) : (
        <>
          <button
            type="button"
            className="kitup-primary-button mt-4 w-full max-w-2xl justify-center"
            disabled={action.isPending}
            onClick={() => {
              // "The whole book" re-narrates every spread, the ones the parent recorded in
              // their own voice included, and that recording is gone: ask first. A refusal
              // changes nothing, so a spread's error stays shown.
              const recorded = items.filter((item) => item.source === 'recorded').length;
              if (recorded > 0 && !window.confirm(t.narration.replaceRecordedConfirm(recorded))) {
                return;
              }
              setError(null);
              action.mutate({
                run: () => narrationsApi.generateAll(bookId, parseVoice(selectedVoice)),
                sceneId: null,
              });
            }}
          >
            {t.narration.generateAll}
          </button>
          {error && errorSceneId === null ? (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          {/*
          Spread by spread is the second way, under the first. Each spread carries its own
          controls: a list that only moved a border, with the controls below it, was a
          desktop pattern that left a phone with nothing visible to do.
        */}
          <h2 className="mt-10 font-semibold">{t.narration.bySpread}</h2>
          <ol className="mt-4 grid gap-3">
            {items.map((item) => (
              <li key={item.sceneId}>
                <NarrationEditor
                  item={item}
                  selectedVoice={selectedVoice}
                  busy={action.isPending}
                  error={errorSceneId === item.sceneId ? error : null}
                  run={(operation) => {
                    setError(null);
                    action.mutate({ run: operation, sceneId: item.sceneId });
                  }}
                />
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

/**
 * One voice: a radio when it can read, its preview, and — for the parent's own voices —
 * its settings behind "Edit", opened in place. A voice without a sample can't be chosen;
 * its settings start open, since adding a sample is the only thing to do with it.
 */
function VoiceRow({
  value,
  name,
  previewUrl,
  selected,
  onSelect,
  voice,
  onChanged,
}: {
  readonly value: string;
  readonly name: string;
  readonly previewUrl: string | null;
  readonly selected: boolean;
  readonly onSelect: (voice: string) => void;
  readonly voice?: VoiceDto;
  readonly onChanged?: () => Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const usable = !voice || voice.status === 'ready';
  const [editing, setEditing] = useState(!usable);
  const settingsId = useId();
  const noteId = useId();
  const noPreview = !previewUrl && !voice;
  return (
    <div
      className={`rounded-lg border p-3 ${
        selected
          ? 'border-[var(--kitup-color-accent)] bg-amber-50 ring-1 ring-[var(--kitup-color-accent)]'
          : 'border-[var(--kitup-color-border)] bg-white'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
        {usable ? (
          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="narration-voice"
              value={value}
              checked={selected}
              aria-describedby={noPreview ? noteId : undefined}
              onChange={() => onSelect(value)}
            />
            <span className="min-w-0 font-medium">{name}</span>
          </label>
        ) : (
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{name}</span>
            <span className="block text-xs text-amber-800">{t.narration.voiceNeedsSample}</span>
          </span>
        )}
        {previewUrl ? <VoicePreviewButton src={previewUrl} name={name} /> : null}
        {voice ? (
          <button
            type="button"
            className="kitup-quiet-button px-2 text-xs"
            aria-expanded={editing}
            aria-controls={settingsId}
            aria-label={
              editing ? t.narration.closeVoiceNamed(name) : t.narration.editVoiceNamed(name)
            }
            onClick={() => setEditing((open) => !open)}
          >
            {editing ? t.common.close : t.narration.editVoice}
          </button>
        ) : null}
      </div>
      {noPreview ? (
        <p id={noteId} className="ml-6 text-xs text-[var(--kitup-color-muted)]">
          {t.narration.previewAfterSynthesis}
        </p>
      ) : null}
      {voice && onChanged && editing ? (
        <div id={settingsId}>
          <VoiceSettings voice={voice} onChanged={onChanged} />
        </div>
      ) : null}
    </div>
  );
}

function VoicePreviewButton({
  src,
  name,
}: {
  readonly src: string;
  readonly name: string;
}): JSX.Element {
  const { t } = useI18n();
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const toggle = (): void => {
    const element = audio.current;
    if (!element) return;
    if (playing) {
      element.pause();
      setPlaying(false);
      return;
    }
    void element.play().then(
      () => setPlaying(true),
      () => setPlaying(false),
    );
  };
  return (
    <>
      <audio ref={audio} src={src} preload="none" onEnded={() => setPlaying(false)} />
      <button
        type="button"
        className="kitup-quiet-button px-2 text-xs"
        aria-label={playing ? t.narration.pauseVoicePreview(name) : t.narration.previewVoice(name)}
        onClick={toggle}
      >
        {playing ? t.narration.pausePreview : t.narration.listen}
      </button>
    </>
  );
}

function VoiceCreator({
  onChanged,
  onDone,
  onCancel,
}: {
  readonly onChanged: () => Promise<void>;
  readonly onDone: (voiceId: string) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const legal = useLegal();
  const [name, setName] = useState('');
  const [method, setMethod] = useState<VoiceSampleMethod>('record');
  const [sample, setSample] = useState<File | null>(null);
  const [sampleMs, setSampleMs] = useState<number | null>(null);
  const [sampleTranscript, setSampleTranscript] = useState('');
  const [transcriptExact, setTranscriptExact] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lengthProblem = sampleLengthProblem(sampleMs, t);
  const nameInput = useRef<HTMLInputElement | null>(null);
  const titleId = useId();

  useEffect(() => nameInput.current?.focus(), []);

  // Measured asynchronously: a measurement of an earlier file that lands late must not
  // speak for the one chosen since.
  const measuring = useRef<File | null>(null);
  const chooseSample = (file: File | null): void => {
    setSample(file);
    setSampleMs(null);
    setError(null);
    measuring.current = file;
    if (file) {
      void measureAudioMs(file).then((ms) => {
        if (measuring.current === file) setSampleMs(ms);
      });
    }
  };
  const changeMethod = (next: VoiceSampleMethod): void => {
    setMethod(next);
    chooseSample(null);
    setSampleTranscript('');
    setTranscriptExact(false);
    setError(null);
  };
  const exactTranscript =
    method === 'upload-other'
      ? transcriptExact
        ? sampleTranscript.trim()
        : null
      : t.narration.referenceScript;
  const canSubmit =
    Boolean(name.trim() && sample && consent && !lengthProblem) &&
    !(method === 'upload-other' && transcriptExact && !sampleTranscript.trim());

  const submit = async (): Promise<void> => {
    if (!sample || !canSubmit) return;
    setBusy(true);
    setError(null);
    // A voice is three requests. If the sample is refused after the voice was created, the
    // voice is deleted again: a nameless half — a voice with no sample — sat in the list
    // while it could not be chosen, and the form said it "already exists".
    let voiceId: string | null = null;
    try {
      voiceId = (await narrationsApi.createVoice(name.trim(), legal.documentVersionId)).id;
      const asset = await uploadAudio(sample, 'voice_sample', voiceId);
      await narrationsApi.attachSample(voiceId, asset.id, exactTranscript);
      await onChanged();
      onDone(voiceId);
    } catch (cause: unknown) {
      if (voiceId) await narrationsApi.deleteVoice(voiceId).catch(() => undefined);
      await onChanged();
      setError(actionError(cause, t, t.narration.actionFailed));
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby={titleId}
      className="rounded-lg border border-[var(--kitup-color-accent)] bg-white p-4"
    >
      <h2 id={titleId} className="font-semibold">
        {t.narration.createVoice}
      </h2>
      <label className="mt-3 grid gap-1 text-sm">
        <span>{t.narration.newVoiceName}</span>
        <input
          ref={nameInput}
          className="kitup-input w-full"
          value={name}
          maxLength={120}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
          placeholder={t.narration.voiceName}
        />
      </label>

      {method === 'upload-other' ? null : <ReferenceScript />}

      <div className="mt-4">
        {method === 'record' ? (
          <>
            <AudioRecorder
              disabled={busy}
              label={sample ? t.narration.rerecordSample : t.narration.recordSample}
              onCaptured={(file) => {
                chooseSample(file);
                return Promise.resolve();
              }}
            />
            <button
              type="button"
              className="mt-2 text-sm underline"
              disabled={busy}
              onClick={() => changeMethod('upload-script')}
            >
              {t.narration.uploadInstead}
            </button>
          </>
        ) : (
          <>
            <fieldset>
              <legend className="text-sm font-medium">{t.narration.fileContents}</legend>
              <div className="mt-2 grid gap-1 text-sm">
                {(
                  [
                    ['upload-script', t.narration.fileIsScript],
                    ['upload-other', t.narration.fileIsOther],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="voice-sample-method"
                      value={value}
                      checked={method === value}
                      disabled={busy}
                      onChange={() => changeMethod(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
            {method === 'upload-script' ? (
              <p className="mt-2 text-xs text-[var(--kitup-color-muted)]">
                {t.narration.fileHowTo}
              </p>
            ) : null}
            {method === 'upload-other' ? (
              <div className="mt-3 grid gap-2">
                <label className="grid gap-1 text-sm">
                  <span>{t.narration.sampleTranscript}</span>
                  <textarea
                    className="kitup-input min-h-24 w-full resize-y"
                    value={sampleTranscript}
                    maxLength={2_000}
                    disabled={busy}
                    onChange={(event) => {
                      setSampleTranscript(event.target.value);
                      if (!event.target.value.trim()) setTranscriptExact(false);
                    }}
                    placeholder={t.narration.sampleTranscriptPlaceholder}
                  />
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={transcriptExact}
                    disabled={busy || !sampleTranscript.trim()}
                    onChange={(event) => setTranscriptExact(event.target.checked)}
                  />
                  <span>{t.narration.transcriptExact}</span>
                </label>
                <p className="text-xs text-[var(--kitup-color-muted)]">
                  {t.narration.transcriptHint}
                </p>
              </div>
            ) : null}
            <label
              className={`kitup-secondary-button mt-3 cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}
            >
              {sample ? t.narration.replaceChosenSample : t.narration.chooseSample}
              <input
                className="sr-only"
                type="file"
                accept={AUDIO_FILE_ACCEPT}
                disabled={busy}
                onChange={(event) => chooseSample(event.currentTarget.files?.[0] ?? null)}
              />
            </label>
            <button
              type="button"
              className="mt-2 block text-sm underline"
              disabled={busy}
              onClick={() => changeMethod('record')}
            >
              {t.narration.recordInstead}
            </button>
          </>
        )}
        {sample && lengthProblem ? (
          <p role="alert" className="mt-2 text-xs text-red-700">
            {lengthProblem}
          </p>
        ) : sample ? (
          <p className="mt-2 break-all text-xs text-emerald-700">
            {sampleMs === null
              ? t.narration.sampleReady(sample.name)
              : t.narration.sampleReadyLength(Math.round(sampleMs / 1_000))}
          </p>
        ) : null}
        <p className="mt-2 text-xs text-[var(--kitup-color-muted)]">{t.narration.sampleHint}</p>
      </div>

      <label className="mt-4 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={consent}
          disabled={busy}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>
          {t.narration.voiceConsent}{' '}
          <Link className="underline" to={legal.routes.voiceConsent} target="_blank">
            {t.narration.voiceConsentLink}
          </Link>
        </span>
      </label>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          className="kitup-secondary-button"
          disabled={busy || !canSubmit}
          onClick={() => void submit()}
        >
          {busy ? t.narration.creatingVoice : t.narration.addVoice}
        </button>
        <button type="button" className="kitup-quiet-button" disabled={busy} onClick={onCancel}>
          {t.common.cancel}
        </button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function ReferenceScript(): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="mt-4 rounded-lg bg-slate-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--kitup-color-muted)]">
        {t.narration.referenceScriptLabel}
      </p>
      <p className="mt-2 text-sm leading-relaxed">{t.narration.referenceScript}</p>
      <p className="mt-2 text-xs text-[var(--kitup-color-muted)]">
        {t.narration.referenceScriptHint}
      </p>
    </div>
  );
}

/** Rename, a new sample, delete: the settings of one of the parent's own voices. */
function VoiceSettings({
  voice,
  onChanged,
}: {
  readonly voice: VoiceDto;
  readonly onChanged: () => Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const [name, setName] = useState(voice.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameSaveState, setNameSaveState] = useState<SaveState>('idle');

  useEffect(() => setName(voice.name), [voice.name]);

  const run = async (operation: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await operation();
      await onChanged();
      return true;
    } catch (cause: unknown) {
      setError(actionError(cause, t, t.narration.actionFailed));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File, sampleTranscript: string | null): Promise<void> => {
    // Busy from the start: measuring takes a moment, and a second tap meanwhile uploaded twice.
    setBusy(true);
    setError(null);
    const problem = sampleLengthProblem(await measureAudioMs(file), t);
    if (problem) {
      setError(problem);
      setBusy(false);
      return;
    }
    await run(async () => {
      const asset = await uploadAudio(file, 'voice_sample', voice.id);
      await narrationsApi.attachSample(voice.id, asset.id, sampleTranscript);
    });
  };

  return (
    <div className="mt-3 grid gap-3 border-t border-[var(--kitup-color-border)] pt-3">
      {voice.sampleTranscript ? (
        <p className="text-xs text-emerald-700">{t.narration.exactTranscriptSaved}</p>
      ) : null}
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const nextName = name.trim();
          if (!nextName || nextName === voice.name) return;
          setNameSaveState('saving');
          void run(() => narrationsApi.renameVoice(voice.id, nextName)).then((saved) =>
            setNameSaveState(saved ? 'saved' : 'idle'),
          );
        }}
      >
        <label className="grid min-w-0 flex-1 gap-1 text-xs">
          <span>{t.narration.renameVoice}</span>
          <input
            className="kitup-input w-full"
            value={name}
            maxLength={120}
            disabled={busy}
            onChange={(event) => {
              setNameSaveState('idle');
              setName(event.target.value);
            }}
          />
        </label>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="submit"
            className="kitup-secondary-button shrink-0"
            disabled={busy || !name.trim() || name.trim() === voice.name}
          >
            {nameSaveState === 'saving' ? t.common.saving : t.narration.saveVoiceName}
          </button>
          {nameSaveState === 'saved' ? (
            <span role="status" className="text-xs text-emerald-700">
              {t.common.saved}
            </span>
          ) : null}
        </div>
      </form>
      <ReferenceScript />
      <div className="flex flex-wrap gap-2">
        <AudioRecorder
          disabled={busy}
          label={voice.sample ? t.narration.rerecordSample : t.narration.recordSample}
          onCaptured={(file) => upload(file, t.narration.referenceScript)}
        />
        <label
          className={`kitup-secondary-button cursor-pointer ${busy ? 'pointer-events-none opacity-50' : ''}`}
        >
          {voice.sample ? t.narration.replaceSample : t.narration.uploadSample}
          <input
            className="sr-only"
            type="file"
            // Расширения перечислены отдельно: для `.opus` и `.oga` система не
            // всегда знает, что это audio/*, и файл не выбрать вовсе.
            accept={AUDIO_FILE_ACCEPT}
            disabled={busy}
            onChange={(event) => {
              const input = event.currentTarget;
              const file = input.files?.[0];
              if (!file) return;
              void upload(file, null).finally(() => {
                input.value = '';
              });
            }}
          />
        </label>
        <button
          type="button"
          className="kitup-quiet-button text-red-700"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(t.narration.deleteVoiceConfirm(voice.name))) return;
            // The row goes with the voice, and the focused button with it: focus moves to the
            // voice now chosen in the list rather than falling to <body>.
            void run(() => narrationsApi.deleteVoice(voice.id)).then((deleted) => {
              if (!deleted) return;
              requestAnimationFrame(() =>
                document
                  .querySelector<HTMLInputElement>('input[name="narration-voice"]:checked')
                  ?.focus(),
              );
            });
          }}
        >
          {t.narration.deleteVoice}
        </button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function NarrationEditor({
  item,
  selectedVoice,
  busy,
  error,
  run,
}: {
  readonly item: NarrationDto;
  readonly selectedVoice: string;
  readonly busy: boolean;
  /** The last action's error, when it came from this spread. */
  readonly error: string | null;
  readonly run: (operation: () => Promise<unknown>) => void;
}): JSX.Element {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const attach = async (file: File): Promise<void> => {
    setUploading(true);
    try {
      const asset = await uploadAudio(file, 'narration_audio');
      await narrationsApi.attachRecorded(item.sceneId, asset.id);
    } finally {
      setUploading(false);
    }
  };
  return (
    <article className="rounded-xl border border-[var(--kitup-color-border)] p-4 sm:p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold">{t.narration.spread(item.sceneIndex + 1)}</h3>
        <span className="text-xs text-[var(--kitup-color-muted)]">
          {t.narration.statuses[item.status]}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-wrap leading-relaxed">{item.textRu}</p>
      {item.narrationStale ? (
        <p className="mt-3 text-sm text-amber-800">{t.narration.stale}</p>
      ) : null}
      {item.lastError ? <GenerationFailed error={item.lastError} className="mt-3" /> : null}
      {item.audio ? <audio className="mt-4 w-full" controls src={item.audio.url} /> : null}
      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <AudioRecorder
          disabled={uploading || busy}
          label={item.audio ? t.narration.rerecord : t.narration.record}
          onCaptured={(file) => {
            run(() => attach(file));
            return Promise.resolve();
          }}
        />
        <label
          className={`kitup-secondary-button cursor-pointer ${uploading || busy ? 'pointer-events-none opacity-50' : ''}`}
        >
          {uploading
            ? t.common.loading
            : item.audio
              ? t.narration.replaceAudio
              : t.narration.uploadAudio}
          <input
            className="sr-only"
            type="file"
            accept={AUDIO_FILE_ACCEPT}
            disabled={uploading || busy}
            onChange={(event) => {
              const input = event.currentTarget;
              const file = input.files?.[0];
              if (!file) return;
              run(async () => {
                try {
                  await attach(file);
                } finally {
                  input.value = '';
                }
              });
            }}
          />
        </label>
        <button
          type="button"
          className="kitup-secondary-button"
          disabled={busy}
          onClick={() => run(() => narrationsApi.generate(item.sceneId, parseVoice(selectedVoice)))}
        >
          {item.source === 'cloned' || item.narrationStale
            ? t.narration.resynthesize
            : t.narration.synthesize}
        </button>
        {item.id ? (
          <button
            type="button"
            className="kitup-quiet-button"
            disabled={busy}
            onClick={() => run(() => narrationsApi.remove(item.sceneId))}
          >
            {t.narration.remove}
          </button>
        ) : null}
      </div>
    </article>
  );
}

function parseVoice(value: string): NarrationVoiceSelection {
  if (value === 'preset:female' || value === 'preset:male') {
    return { kind: 'preset', presetVoice: value === 'preset:female' ? 'female' : 'male' };
  }
  return { kind: 'custom', voiceId: value.slice('custom:'.length) };
}

/**
 * The one microphone capture on the page. Every spread now has its own recorder on screen:
 * starting one discards any other's take, unsaved, so two captures never run at once and a
 * half-finished take never replaces what a spread or a voice already had.
 */
let activeRecording: { readonly abandon: () => void } | null = null;

function AudioRecorder({
  disabled,
  label,
  onCaptured,
}: {
  readonly disabled: boolean;
  readonly label: string;
  readonly onCaptured: (file: File) => Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);
  // Between the tap and the browser's microphone prompt being answered.
  const [starting, setStarting] = useState(false);
  // Each start is one attempt; abandoning or leaving the page ends it, so a prompt answered
  // afterwards releases the microphone instead of recording with no button left to stop it.
  const attempt = useRef(0);
  const [permissionError, setPermissionError] = useState(false);
  const [captureError, setCaptureError] = useState(false);
  // Stops without saving and releases the microphone: for another recorder starting, and for
  // closing the form or a voice's settings mid-recording (an unmount).
  const abandon = (): void => {
    attempt.current += 1;
    if (recorder.current?.state === 'recording') {
      recorder.current.onstop = null;
      recorder.current.stop();
    }
    stream.current?.getTracks().forEach((track) => track.stop());
    if (activeRecording === handle.current) activeRecording = null;
  };
  // This recorder's entry in `activeRecording`: stable across renders, calling the latest abandon.
  const abandonRef = useRef(abandon);
  abandonRef.current = abandon;
  const handle = useRef({
    abandon: () => {
      abandonRef.current();
      setRecording(false);
      setStarting(false);
    },
  });
  useEffect(() => () => abandonRef.current(), []);
  const start = async (): Promise<void> => {
    setPermissionError(false);
    activeRecording?.abandon();
    const mine = ++attempt.current;
    activeRecording = handle.current;
    setStarting(true);
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1 } });
      if (mine !== attempt.current) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      const next = new MediaRecorder(media, { mimeType: 'audio/webm' });
      stream.current = media;
      recorder.current = next;
      chunks.current = [];
      next.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data);
      };
      next.onstop = () => {
        const file = new File(chunks.current, `recording-${Date.now()}.webm`, {
          type: 'audio/webm',
        });
        stream.current?.getTracks().forEach((track) => track.stop());
        void onCaptured(file).catch(() => setCaptureError(true));
      };
      next.start();
      setRecording(true);
    } catch {
      if (mine !== attempt.current) return;
      setPermissionError(true);
      if (activeRecording === handle.current) activeRecording = null;
    } finally {
      if (mine === attempt.current) setStarting(false);
    }
  };
  const stop = (): void => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
    setRecording(false);
    if (activeRecording === handle.current) activeRecording = null;
  };
  return (
    <div>
      <button
        type="button"
        className="kitup-secondary-button"
        // A running recording can always be stopped, whatever else is busy; one waiting on the
        // microphone prompt cannot be started twice.
        disabled={(disabled && !recording) || starting}
        onClick={recording ? stop : () => void start()}
      >
        {recording ? t.narration.stopRecording : label}
      </button>
      {permissionError ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {t.narration.microphoneDenied}
        </p>
      ) : null}
      {captureError ? (
        <p role="alert" className="mt-2 text-xs text-red-700">
          {t.narration.actionFailed}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Сообщение об ошибке действия.
 *
 * Раньше здесь показывался `cause.message`, то есть текст ошибки от сервера.
 * У апи он на языке бренда и вообще-то диагностический — англоязычный
 * пользователь увидел бы русскую фразу. Поэтому текст берётся из словаря
 * по коду, ровно как во всём остальном интерфейсе.
 */
function actionError(cause: unknown, t: Dictionary, fallback: string): string {
  if (cause instanceof ApiError || cause instanceof NetworkError) {
    return userErrorMessage(cause, t);
  }
  return fallback;
}
